import React from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import schoolLogo from '../../assets/school-logo-header.png';

interface SidebarItem {
    name: string;
    path: string;
    icon: React.ReactNode;
}

interface SidebarProps {
    items: SidebarItem[];
    isOpen: boolean;
    onToggle: () => void;
    onLogout: () => void;
    title?: string;
    userEmail?: string;
    userName?: string;
    mobile?: boolean;
    className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
    items,
    isOpen,
    onToggle,
    onLogout,
    title = 'Admin Panel',
    userEmail,
    userName,
    mobile = false,
    className = '',
}) => {
    // Generate user initials for avatar
    const userInitials = userName
        ? userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
        : userEmail?.charAt(0).toUpperCase() || 'A';

    if (mobile) {
        // Mobile slide-in drawer
        return (
            <aside
                className={`
                    fixed inset-y-0 left-0 w-[280px] z-50
                    bg-[#0A0A0A]/95 backdrop-blur-2xl border-r border-white/[0.06]
                    transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    ${className}
                `}
            >
                {/* Header */}
                <div className="h-[72px] flex items-center justify-between px-4 border-b border-white/[0.04]">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06] shrink-0">
                            <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                        </div>
                        <div className="flex flex-col">
                            <span className="font-display font-bold text-white text-sm tracking-[0.2em]">ENIGMA</span>
                            <span className="text-brand text-[10px] font-bold">'26</span>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="px-3 py-4 space-y-1 overflow-y-auto max-h-[calc(100vh-220px)] scrollbar-none">
                    {items.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={onToggle}
                            className={({ isActive }) => `
                                relative flex items-center rounded-xl transition-all duration-200 group
                                ${isActive
                                    ? 'bg-brand/[0.08] text-white before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-6 before:bg-brand before:rounded-full'
                                    : 'text-white/40 hover:bg-white/[0.04] hover:text-white/70'
                                }
                            `}
                        >
                            {({ isActive }) => (
                                <>
                                    <div className={`w-[48px] h-[44px] flex items-center justify-center shrink-0 transition-colors duration-200 ${isActive ? 'text-brand' : ''}`}>
                                        {item.icon}
                                    </div>
                                    <span className={`text-sm font-medium ${isActive ? 'text-white' : ''}`}>
                                        {item.name}
                                    </span>
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Footer */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="rounded-xl bg-white/[0.03] border border-white/[0.04] p-3">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand to-info p-[2px] shrink-0">
                                <div className="w-full h-full rounded-full bg-deep-black flex items-center justify-center text-xs font-bold text-white">
                                    {userInitials}
                                </div>
                            </div>
                            <div className="flex flex-col overflow-hidden flex-1 min-w-0">
                                <span className="text-sm font-semibold text-white truncate">{userName || 'Admin User'}</span>
                                <span className="text-[10px] text-white/30 truncate">{userEmail}</span>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-success" />
                                    <span className="text-[10px] text-white/40 uppercase tracking-wide">Online</span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onLogout}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-white/40 hover:text-danger hover:bg-danger/[0.06] transition-all duration-200 text-xs"
                        >
                            <LogOut size={16} />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </aside>
        );
    }

    // Desktop floating glass rail
    return (
        <aside
            className={`
                fixed top-3 bottom-3 left-3 rounded-2xl z-40
                bg-[#0A0A0A]/80 backdrop-blur-2xl border border-white/[0.06]
                shadow-[0_8px_32px_rgba(0,0,0,0.4)]
                transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
                ${isOpen ? 'w-[260px]' : 'w-[72px]'}
                ${className}
            `}
        >
            {/* Header Section */}
            <div className="h-[72px] flex items-center justify-between px-3 relative">
                <div className={`flex items-center gap-3 transition-all duration-300 overflow-hidden ${isOpen ? 'opacity-100' : 'opacity-0 w-0'}`}>
                    <div className="w-9 h-9 rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06] shrink-0">
                        <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-display font-bold text-white text-sm tracking-[0.2em] whitespace-nowrap">ENIGMA</span>
                        <span className="text-brand text-[10px] font-bold">'26</span>
                    </div>
                </div>

                {/* Centered logo when collapsed */}
                {!isOpen && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-9 h-9 rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06]">
                            <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                        </div>
                    </div>
                )}

                {/* Toggle button */}
                <button
                    onClick={onToggle}
                    className={`w-7 h-7 rounded-full bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] flex items-center justify-center text-white/40 hover:text-white/70 transition-all duration-200 shrink-0 ${!isOpen && 'absolute -right-3 top-1/2 -translate-y-1/2'}`}
                >
                    {isOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
                </button>
            </div>

            {/* Separator */}
            <div className="border-b border-white/[0.04] mx-4" />

            {/* Navigation Section */}
            <nav className="px-3 py-4 space-y-1 overflow-y-auto max-h-[calc(100vh-220px)] scrollbar-none">
                {items.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) => `
                            relative flex items-center rounded-xl transition-all duration-200 group
                            ${isActive
                                ? 'bg-brand/[0.08] text-white before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-6 before:bg-brand before:rounded-full'
                                : 'text-white/40 hover:bg-white/[0.04] hover:text-white/70'
                            }
                        `}
                    >
                        {({ isActive }) => (
                            <>
                                <div className={`w-[48px] h-[44px] flex items-center justify-center shrink-0 transition-colors duration-200 ${isActive ? 'text-brand' : ''}`}>
                                    {item.icon}
                                </div>
                                <span className={`text-sm font-medium transition-all duration-300 whitespace-nowrap ${isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 absolute'} ${isActive ? 'text-white' : ''}`}>
                                    {item.name}
                                </span>

                                {/* Tooltip for collapsed state */}
                                {!isOpen && (
                                    <div className="absolute left-[calc(100%+8px)] px-3 py-1.5 bg-surface border border-white/[0.08] rounded-lg text-xs text-white/80 opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap shadow-elevated z-50 transition-opacity duration-150">
                                        {item.name}
                                    </div>
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            {/* Footer / User Section */}
            <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.04] p-3">
                    {/* Expanded view */}
                    {isOpen && (
                        <>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand to-info p-[2px] shrink-0">
                                    <div className="w-full h-full rounded-full bg-deep-black flex items-center justify-center text-xs font-bold text-white">
                                        {userInitials}
                                    </div>
                                </div>
                                <div className="flex flex-col overflow-hidden flex-1 min-w-0">
                                    <span className="text-sm font-semibold text-white truncate">{userName || 'Admin User'}</span>
                                    <span className="text-[10px] text-white/30 truncate">{userEmail}</span>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-success" />
                                        <span className="text-[10px] text-white/40 uppercase tracking-wide">Online</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={onLogout}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-white/40 hover:text-danger hover:bg-danger/[0.06] transition-all duration-200 text-xs"
                            >
                                <LogOut size={16} />
                                <span>Logout</span>
                            </button>
                        </>
                    )}

                    {/* Collapsed view */}
                    {!isOpen && (
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand to-info p-[2px]">
                                <div className="w-full h-full rounded-full bg-deep-black flex items-center justify-center text-xs font-bold text-white">
                                    {userInitials}
                                </div>
                            </div>
                            <button
                                onClick={onLogout}
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-white/40 hover:text-danger hover:bg-danger/[0.06] transition-all duration-200"
                            >
                                <LogOut size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};
